import { FC, CSSProperties, useMemo } from 'react';
import classNames from 'classnames';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import InfoPopover from 'shared/components/andtComponents/InfoPopover';
import Spinner, { SPINNER_SIZES } from 'shared/components/andtComponents/Spinner';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import { getCloudTypeSquareIcon } from 'app/store/vendorsStore/vendorStoreProvider';
import { numberWithCommas } from 'shared/utils/strUtil';

import styles from './GovernanceTagsMetricBlock.module.scss';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';

interface MetricValue {
  money: number;
  count: number;
}

interface MetricsData {
  tagCoverage: { numerator: MetricValue; denominator: MetricValue };
  untagged30d: MetricValue;
  untaggedAnnual: MetricValue;
  coveragePercentage: number;
}

interface MetricBlocksProps {
  isLoadingMetricData: boolean;
  metrics?: MetricsData | null;
  currDispUserCloudAccountType?: number | null;
}

const COVERAGE_THRESHOLD = {
  LOW: 50,
  MEDIUM: 80,
};

const GovernanceTagsMetricBlocks: FC<MetricBlocksProps> = ({
  isLoadingMetricData,
  metrics,
  currDispUserCloudAccountType,
}) => {
  const inlineSpinnerStyle: CSSProperties = { position: 'static', top: 'auto', left: 'auto', transform: 'none' };
  const coveragePercentage = metrics?.coveragePercentage ?? 0;

  const untaggedIcon = useMemo(() => {
    const IconComponent = getCloudTypeSquareIcon(currDispUserCloudAccountType ?? 0);
    return <IconComponent className={styles.metricIcon} />;
  }, [currDispUserCloudAccountType]);

  const coverageIcon = useMemo(() => {
    if (coveragePercentage < COVERAGE_THRESHOLD.LOW) {
      return <GenerateIcon iconName={ICONS.bolt.name} className={styles.badgeIcon} />;
    } else if (coveragePercentage < COVERAGE_THRESHOLD.MEDIUM) {
      return <GenerateIcon iconName={ICONS.circleInfo.name} className={styles.badgeIcon} />;
    } else {
      return <GenerateIcon iconName={ICONS.circleCheckSolid.name} className={styles.badgeIcon} />;
    }
  }, [coveragePercentage]);

  const RenderCoverageBlock = () => (
    <div className={styles.metricBlock}>
      <div className={styles.metricHeader}>
        <div className={styles.metricIcon}>
          <GenerateIcon iconName={ICONS.tagsSolid.name} className={styles.innerTagsSolidIcon} />
        </div>
        <h5 className={styles.metricTitle}>Virtual Tags Coverage</h5>
        <InfoPopover
          placement="top"
          mode="outline"
          className={{
            icon: styles.infoIcon,
            tooltip: tooltipStyles.whiteTooltip,
            arrow: tooltipStyles.whiteArrow,
          }}
        >
          The difference between resources that are untagged in the cloud provider and those tagged with Virtual Tags. A
          higher gap indicates a discrepancy in tagging coverage, which may affect cost tracking and allocation
          accuracy.
        </InfoPopover>
      </div>
      <div className={styles.metricValueContainer}>
        {isLoadingMetricData ? (
          <div className={styles.spinnerContainer}>
            <Spinner size={SPINNER_SIZES.SMALL} style={inlineSpinnerStyle} />
          </div>
        ) : (
          <>
            <span className={styles.metricMoneyValue}>
              ${numberWithCommas(metrics?.tagCoverage.numerator.money ?? 0, 2)}
              <span className={styles.metricCountValue}>
                ({numberWithCommas(metrics?.tagCoverage.numerator.count ?? 0, 0)}){' '}
              </span>
            </span>
            <span className={styles.separator}> / </span>
            <span className={styles.metricMoneyValue}>
              ${numberWithCommas(metrics?.tagCoverage.denominator.money ?? 0, 2)}
              <span className={styles.metricCountValue}>
                ({numberWithCommas(metrics?.tagCoverage.denominator.count ?? 0, 0)}){' '}
              </span>
            </span>
            <div
              className={classNames(styles.coverageBadge, {
                [styles.danger]: coveragePercentage < COVERAGE_THRESHOLD.LOW,
                [styles.warning]:
                  coveragePercentage >= COVERAGE_THRESHOLD.LOW && coveragePercentage < COVERAGE_THRESHOLD.MEDIUM,
                [styles.positive]: coveragePercentage >= COVERAGE_THRESHOLD.MEDIUM,
              })}
            >
              <Tooltip
                title="Coverage %"
                classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
                placement="top"
              >
                <span>{coveragePercentage}%</span>
                {coverageIcon}
              </Tooltip>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const RenderUntaggedBlock = () => (
    <div className={styles.metricBlock}>
      <div className={styles.metricHeader}>
        {untaggedIcon}
        <h5 className={styles.metricTitle}>Untagged Resources ($/#)</h5>
      </div>
      <div className={styles.metricValueContainer}>
        {isLoadingMetricData ? (
          <div className={styles.spinnerContainer}>
            <Spinner size={SPINNER_SIZES.SMALL} style={inlineSpinnerStyle} />
          </div>
        ) : (
          <div className={styles.untaggedMetrics}>
            <div className={styles.untaggedMetricItem}>
              <span className={styles.metricLabel}>Last 30d:</span>
              <span className={styles.metricMoneyValue}>
                ${numberWithCommas(metrics?.untagged30d.money ?? 0, 2)}
                <span className={styles.metricCountValue}>
                  ({numberWithCommas(metrics?.untagged30d.count ?? 0, 0)})
                </span>
              </span>
            </div>
            <div className={styles.untaggedMetricItem}>
              <span className={styles.metricLabel}>Annual:</span>
              <span className={styles.metricMoneyValue}>
                ${numberWithCommas(metrics?.untaggedAnnual.money ?? 0, 2)}
                <span className={styles.metricCountValue}>
                  ({numberWithCommas(metrics?.untaggedAnnual.count ?? 0, 0)})
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.metricBlocksWrapper}>
      {RenderCoverageBlock()}
      {RenderUntaggedBlock()}
    </div>
  );
};

export default GovernanceTagsMetricBlocks;
